'use client';

import { RestApiItemDto } from '@order-mage/integration-sdk';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import {
    getAddonPillLabel,
    getAddonPills,
    processAddonChanges,
    resolveEffectiveAddons,
    selectedAddonLines,
    type AddonChangeLine,
    type AddonPill,
} from '@/entities/cart';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { Link } from '@/shared/i18n/navigation';
import { getMainAsset } from '@/shared/lib/getMainAsset';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { LocalizedString } from '@/shared/model/Language';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './added-to-cart-modal.scss';

type Props = ModalProps & {
    hash: string;
    variant: string | null;
    addons: Record<string, string | Record<string, number>>;
    memberVariants?: Record<string, string>;
    quantity: number;
    children?: ReactNode;
};

type AddedToCartModalContextValue = {
    quantity: number;
    localizedName: string;
    variantName: string | null;
    variantItemId: string | null;
    added: { count: number; name: LocalizedString }[];
    removed: { name: LocalizedString }[];
    lines: AddonChangeLine[];
    mainAsset: { fullUrl: string } | null;
    onClose: ModalProps['onClose'];
    getLocalizedValue: ReturnType<typeof useGetLocalizedValue>;
    getPageUrl: ReturnType<typeof useGetPageUrl>;
};

const AddedToCartModalContext = createContext<AddedToCartModalContextValue | null>(null);

const useAddedToCartModalContext = () => {
    const context = useContext(AddedToCartModalContext);
    if (!context) {
        throw new Error('AddedToCartModal components must be used within AddedToCartModalWrapper.');
    }
    return context;
};

export const AddedToCartModalWrapper = ({
    hash,
    variant,
    addons,
    memberVariants,
    quantity,
    children,
    ...props
}: Props) => {
    const getLocalizedValue = useGetLocalizedValue();
    const getPageUrl = useGetPageUrl();
    const itemsLookup = useStorefrontItemsIndex();

    // Find the item by hash from items map
    const item = itemsLookup[hash];

    if (!item) {
        return null;
    }

    const itemData = item.body as never as RestApiItemDto;
    const itemAddons = resolveEffectiveAddons(itemsLookup, item.body.addons as never, variant, hash);

    // Process addons to determine changes
    const { added, removed } =
        itemAddons && addons
            ? processAddonChanges(itemAddons, addons, itemsLookup)
            : { added: [], removed: [] };
    const lines =
        itemAddons && addons ? selectedAddonLines(itemAddons, addons, itemsLookup, memberVariants) : [];

    const localizedName = getLocalizedValue(itemData.name);
    const mainAsset = getMainAsset(itemData.assets);

    // Get variant name and variantItemId if variant is selected
    const variantItem = variant && variant !== hash && itemsLookup[variant] ? itemsLookup[variant] : null;

    const variantName = variantItem ? getLocalizedValue(variantItem.body.name) : null;

    // Find variantItemId from itemData.variants by matching variantItem.body.id
    const variantItemId = variantItem?.placement.variantItemId ?? variantItem?.body.id ?? null;

    return (
        <AddedToCartModalContext.Provider
            value={{
                quantity,
                localizedName,
                variantName,
                variantItemId,
                added,
                removed,
                lines,
                mainAsset,
                onClose: props.onClose,
                getLocalizedValue,
                getPageUrl,
            }}
        >
            <Modal
                {...props}
                wrapperProps={{
                    className: 'added-to-cart-modal__wrapper',
                    'data-testid': 'added-to-cart-modal',
                }}
            >
                {children}
            </Modal>
        </AddedToCartModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). Returns null if item not found. */
export const AddedToCartModalProvider = ({
    hash,
    variant,
    addons,
    memberVariants,
    quantity,
    children,
    ...props
}: Props) => {
    const getLocalizedValue = useGetLocalizedValue();
    const getPageUrl = useGetPageUrl();
    const itemsLookup = useStorefrontItemsIndex();

    const item = itemsLookup[hash];
    if (!item) {
        return null;
    }

    const itemData = item.body as never as RestApiItemDto;
    const itemAddons = resolveEffectiveAddons(itemsLookup, item.body.addons as never, variant, hash);

    // Process addons to determine changes
    const { added, removed } =
        itemAddons && addons
            ? processAddonChanges(itemAddons, addons, itemsLookup)
            : { added: [], removed: [] };
    const lines =
        itemAddons && addons ? selectedAddonLines(itemAddons, addons, itemsLookup, memberVariants) : [];

    const localizedName = getLocalizedValue(itemData.name);
    const mainAsset = getMainAsset(itemData.assets);

    const variantItem = variant && variant !== hash && itemsLookup[variant] ? itemsLookup[variant] : null;

    const variantName = variantItem ? getLocalizedValue(variantItem.body.name) : null;
    const variantItemId = variantItem?.placement.variantItemId ?? variantItem?.body.id ?? null;

    return (
        <AddedToCartModalContext.Provider
            value={{
                quantity,
                localizedName,
                variantName,
                variantItemId,
                added,
                removed,
                lines,
                mainAsset,
                onClose: props.onClose,
                getLocalizedValue,
                getPageUrl,
            }}
        >
            {children}
        </AddedToCartModalContext.Provider>
    );
};

type TopPanelProps = ComponentProps<typeof Modal.FixedTopPanel>;
const AddedToCartModalTopPanel = ({ children, ...props }: TopPanelProps) => {
    return <Modal.FixedTopPanel {...props}>{children}</Modal.FixedTopPanel>;
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const AddedToCartModalTitle = () => {
    const t = useTranslations();
    return <>{t('ADDED_TO_CART_MODAL.TITLE')}</>;
};

type ImageProps = ComponentProps<typeof Layout>;
const AddedToCartModalImage = ({ media, ...props }: ImageProps) => {
    const { mainAsset, localizedName } = useAddedToCartModalContext();
    if (!mainAsset) {
        return null;
    }
    return (
        <Layout media={media} data-testid="added-to-cart-modal-image" {...props}>
            {mainAsset ? (
                <Media
                    data={{ url: mainAsset.fullUrl, alt: `${localizedName}-image` }}
                    width={109}
                    height={109}
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
            ) : null}
        </Layout>
    );
};

type NameProps = ComponentProps<typeof Layout>;
const AddedToCartModalName = ({ media, ...props }: NameProps) => {
    const { quantity, localizedName } = useAddedToCartModalContext();
    return (
        <Layout
            element="h3"
            media={media}
            data-testid="added-to-cart-modal-name"
            data-quantity={quantity}
            {...props}
        >
            {quantity}x {localizedName}
        </Layout>
    );
};

type VariantProps = ComponentProps<typeof Layout>;
const AddedToCartModalVariant = ({ media, ...props }: VariantProps) => {
    const { variantName, variantItemId } = useAddedToCartModalContext();
    if (!variantName) {
        return null;
    }
    return (
        <Layout
            element="span"
            media={media}
            data-testid="added-to-cart-modal-variant"
            data-variantitemid={variantItemId ?? undefined}
            {...props}
        >
            {variantName}
        </Layout>
    );
};

type ModificationsProps = Omit<ComponentProps<typeof Layout>, 'children'> & {
    labelMedia?: ComponentProps<typeof Layout>['media'];
    itemsMedia?: ComponentProps<typeof Layout>['media'];
};

const AddedToCartModalAddedModifications = ({
    media,
    labelMedia,
    itemsMedia,
    ...props
}: ModificationsProps) => {
    const t = useTranslations();
    const { added, getLocalizedValue } = useAddedToCartModalContext();
    if (!added.length) {
        return null;
    }
    return (
        <Layout media={media} {...props}>
            <Layout element="span" media={labelMedia} data-testid="added-to-cart-modal-added-label">
                {t('WORDS.ADDED')}
            </Layout>
            <Layout element="span" media={itemsMedia} data-testid="added-to-cart-modal-added-items">
                {added.map(({ count, name }, idx) => (
                    <span key={idx} data-testid="added-to-cart-modal-added-item" data-count={count}>
                        {count > 1 ? `${count}x ` : ''}
                        {getLocalizedValue(name)}
                        {idx < added.length - 1 ? ', ' : ''}
                    </span>
                ))}
            </Layout>
        </Layout>
    );
};

const AddedToCartModalModifications = () => {
    const t = useTranslations();
    const { lines, getLocalizedValue } = useAddedToCartModalContext();
    const { added, removed } = getAddonPills(lines);
    if (!added.length && !removed.length) return null;
    const renderPills = (rows: AddonPill[], removedRow: boolean) =>
        rows.map((row, idx) => (
            <span
                key={`${removedRow ? 'removed' : 'added'}-${idx}`}
                className={
                    removedRow
                        ? 'added-to-cart-modal__mod-pill added-to-cart-modal__mod-pill--removed'
                        : 'added-to-cart-modal__mod-pill'
                }
                data-testid={
                    removedRow ? 'added-to-cart-modal-removed-item' : 'added-to-cart-modal-added-item'
                }
            >
                <span>{removedRow ? '-' : '+'}</span>
                <span>{getAddonPillLabel(row, getLocalizedValue)}</span>
            </span>
        ));
    return (
        <div className="added-to-cart-modal__mods" data-testid="added-to-cart-modal-addons">
            <span className="added-to-cart-modal__mod-label">{t('WORDS.MODIFICATIONS')}</span>
            {renderPills(added, false)}
            {renderPills(removed, true)}
        </div>
    );
};

const AddedToCartModalRemovedModifications = ({
    media,
    labelMedia,
    itemsMedia,
    ...props
}: ModificationsProps) => {
    const t = useTranslations();
    const { removed, getLocalizedValue } = useAddedToCartModalContext();
    if (!removed.length) {
        return null;
    }
    return (
        <Layout media={media} {...props}>
            <Layout element="span" media={labelMedia} data-testid="added-to-cart-modal-removed-label">
                {t('WORDS.REMOVED')}
            </Layout>
            <Layout element="span" media={itemsMedia} data-testid="added-to-cart-modal-removed-items">
                {removed.map(({ name }, idx) => (
                    <span key={idx} data-testid="added-to-cart-modal-removed-item">
                        {getLocalizedValue(name)}
                        {idx < removed.length - 1 ? ', ' : ''}
                    </span>
                ))}
            </Layout>
        </Layout>
    );
};

type ActionButtonProps = ComponentProps<typeof Button>;
const AddedToCartModalBackButton = ({ onClick, ...props }: ActionButtonProps) => {
    const t = useTranslations();
    const { onClose } = useAddedToCartModalContext();
    return (
        <Layout
            element={Button}
            variant="secondary"
            data-testid="added-to-cart-modal-back-button"
            onClick={(event) => {
                onClick?.(event);
                onClose();
            }}
            {...props}
        >
            <span data-testid="added-to-cart-modal-back-button-text">{t('WORDS.BACK')}</span>
        </Layout>
    );
};

type GoToCartProps = Omit<ComponentProps<typeof Button>, 'asChild'>;
const AddedToCartModalGoToCartButton = ({ ...props }: GoToCartProps) => {
    const t = useTranslations();
    const { onClose, getPageUrl } = useAddedToCartModalContext();
    return (
        <Layout element={Button} asChild {...props}>
            <Link
                href={getPageUrl('cart')!}
                data-testid="added-to-cart-modal-go-to-cart-button"
                onClick={() => onClose()}
            >
                <span data-testid="added-to-cart-modal-go-to-cart-button-text">
                    {t('ADDED_TO_CART_MODAL.GO_TO_CART_BUTTON')}
                </span>
            </Link>
        </Layout>
    );
};

export { AddedToCartModalWrapper as AddedToCartModal };

export {
    AddedToCartModalWrapper as Wrapper,
    AddedToCartModalProvider as Provider,
    AddedToCartModalTopPanel as TopPanel,
    AddedToCartModalTitle as Title,
    AddedToCartModalImage as Image,
    AddedToCartModalName as Name,
    AddedToCartModalVariant as Variant,
    AddedToCartModalAddedModifications as AddedModifications,
    AddedToCartModalRemovedModifications as RemovedModifications,
    AddedToCartModalModifications as Modifications,
    AddedToCartModalBackButton as BackButton,
    AddedToCartModalGoToCartButton as GoToCartButton,
};
